import React, { useState } from 'react';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { LanguageSelector } from '../components/LanguageSelector';

interface LocalGameCardsPageProps {
  players: string[];
  word: string;
  category: string;
  impostorIndices: number[];
  onBack: () => void;
}

interface CardState {
  isRevealed: boolean;
  hasBeenFlipped: boolean;
  flipCount: number;
}

export const LocalGameCardsPage: React.FC<LocalGameCardsPageProps> = ({
  players,
  word,
  category,
  impostorIndices,
  onBack,
}) => {
  const [cardStates, setCardStates] = useState<CardState[]>(
    players.map(() => ({
      isRevealed: false,
      hasBeenFlipped: false,
      flipCount: 0,
    }))
  );

  const handleCardClick = (index: number) => {
    const currentState = cardStates[index];

    // Si ya se volteó 2 veces (una para ver, otra para ocultar), no hacer nada
    if (currentState.flipCount >= 2) {
      return;
    }

    setCardStates((prev) =>
      prev.map((state, i) =>
        i === index
          ? {
              ...state,
              isRevealed: !state.isRevealed,
              hasBeenFlipped: true,
              flipCount: state.flipCount + 1,
            }
          : state
      )
    );
  };

  const isImpostor = (index: number) => impostorIndices.includes(index);

  const allCardsChecked = cardStates.every((state) => state.hasBeenFlipped);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20 pointer-events-none"></div>

      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          Volver
        </Button>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-6xl animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display">
            🃏 Cartas de Jugadores
          </h1>
          <p className="text-white/70 text-lg mb-2">
            📚 Categoría: <span className="text-primary-400 font-semibold">{category}</span>
          </p>
          <p className="text-white/60 text-sm">
            👆 Cada jugador toca su carta para ver su rol (solo puedes voltearla 2 veces)
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {players.map((playerName, index) => {
            const state = cardStates[index];
            const impostor = isImpostor(index);
            const isLocked = state.flipCount >= 2;

            return (
              <button
                key={index}
                onClick={() => handleCardClick(index)}
                disabled={isLocked && !state.isRevealed}
                className={`group relative aspect-[3/4] rounded-2xl transition-all duration-500 transform-gpu ${
                  isLocked && !state.isRevealed
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:scale-105 cursor-pointer'
                }`}
                style={{
                  perspective: '1000px',
                }}
              >
                <div
                  className={`relative w-full h-full transition-transform duration-700 transform-gpu`}
                  style={{
                    transformStyle: 'preserve-3d',
                    transform: state.isRevealed ? 'rotateY(180deg)' : 'rotateY(0deg)',
                  }}
                >
                  {/* Card Back (Face Down) */}
                  <div
                    className="absolute inset-0 rounded-2xl border-4 border-white/20 bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex flex-col items-center justify-center shadow-2xl"
                    style={{
                      backfaceVisibility: 'hidden',
                    }}
                  >
                    <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mb-4">
                      <span className="text-4xl">🎴</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{playerName}</h3>
                    <p className="text-white/60 text-sm">Toca para revelar</p>
                    {isLocked && (
                      <div className="absolute top-4 right-4 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">🔒</span>
                      </div>
                    )}
                  </div>

                  {/* Card Front (Face Up) */}
                  <div
                    className={`absolute inset-0 rounded-2xl border-4 ${
                      impostor ? 'border-accent-400 bg-gradient-to-br from-red-900 via-red-800 to-red-950' : 'border-success-400 bg-gradient-to-br from-green-900 via-green-800 to-green-950'
                    } flex flex-col items-center justify-center shadow-2xl p-6`}
                    style={{
                      backfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                    }}
                  >
                    <div className={`w-20 h-20 rounded-full ${impostor ? 'bg-accent-500' : 'bg-success-500'} flex items-center justify-center mb-4`}>
                      {impostor ? (
                        <EyeOff className="w-10 h-10 text-white" />
                      ) : (
                        <Eye className="w-10 h-10 text-white" />
                      )}
                    </div>

                    <h3 className="text-2xl font-bold text-white mb-4">{playerName}</h3>

                    {impostor ? (
                      <>
                        <p className="text-accent-400 text-xl font-bold mb-2">🎭 ERES EL IMPOSTOR</p>
                        <p className="text-white/80 text-center text-sm">
                          Intenta adivinar la palabra de los demás sin que te descubran
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-success-400 text-lg font-semibold mb-3">Tu palabra es:</p>
                        <div className="bg-white/10 rounded-xl px-6 py-4 backdrop-blur-sm">
                          <p className="text-white text-3xl font-bold">{word}</p>
                        </div>
                        <p className="text-white/60 text-sm mt-4">
                          Descríbela sin mencionarla directamente
                        </p>
                      </>
                    )}

                    <p className="text-white/40 text-xs mt-4">
                      {state.flipCount === 1 ? 'Puedes ocultarla 1 vez más' : 'Carta bloqueada'}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Info Card */}
        {allCardsChecked && (
          <Card className="text-center animate-fade-in">
            <h3 className="text-xl font-bold text-white mb-2">
              ✅ Todos han visto su carta
            </h3>
            <p className="text-white/70 mb-4">
              ¡Ahora pueden comenzar a jugar! Descubran quién es el impostor.
            </p>
            <Button variant="primary" size="lg" onClick={onBack}>
              Nueva Partida
            </Button>
          </Card>
        )}

        {/* Instructions */}
        <Card variant="glass" className="mt-6">
          <h4 className="text-white font-semibold mb-2">📖 Instrucciones:</h4>
          <ul className="text-white/70 text-sm space-y-1">
            <li>• Cada jugador toca su carta para ver su rol</li>
            <li>• Puedes voltear tu carta 2 veces: una para ver, otra para ocultar</li>
            <li>• Los jugadores normales ven la palabra secreta</li>
            <li>• El impostor debe adivinar la palabra sin que lo descubran</li>
            <li>• ¡Discutan y descubran quién es el impostor!</li>
          </ul>
        </Card>
      </div>
    </div>
  );
};
